import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';

import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import EmailIcon from '@material-ui/icons/Email';
import CallIcon from '@material-ui/icons/Call';
import ChatIcon from '@material-ui/icons/Chat';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';

import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { useTheme } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';

import { isBranchEnv, appConfig } from 'brand-config';
import { openFrontChatPopup } from 'core/components/front-chat';
import AgencyHelpModal from './agency-help-modal/agency-help-modal';

const useStyles = makeStyles((theme) => ({
  floatingContainer: ({ quotePage }) => ({
    position: quotePage ? 'absolute' : 'fixed',
    bottom: quotePage ? undefined : '200px',
    top: quotePage ? '-208px' : undefined,
    right: '35px',
    zIndex: 200
  }),
  tooltip: {
    backgroundColor: theme.colorPalette.green_75
  }
}));

const StaffSpeedDial = ({ quotePage = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const classes = useStyles({ quotePage });
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const session = useSession();
  const [openAgencyHelpModal, setOpenAgencyHelpModal] = useState(false);
  const URL = `https://${window.location.host}${history.location.pathname}`;
  const affinity = session?.user?.affinityCode;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = () => {
    // keeps the chat from closing if user clicks on it
    setOpen(!!open);
  };

  const openChat = () => {
    track('Open agency support chat', {
      URL,
      affinity
    });

    openFrontChatPopup();
  };

  const handleBugReport = () => {
    track('Contact Agency support via typeform', {
      URL,
      affinity
    });

    window.open('https://ourbranch.typeform.com/to/QsfFE7NK', '_blank', 'noopener,noreferrer');
  };

  const openFnolForm = () => {
    track('Open FNOL form', {
      URL,
      affinity
    });

    window.open(appConfig.helpWidget.fnolUrl, '_blank', 'noopener,noreferrer');
  };

  const showAgencyModal = () => {
    track('Contact Agency support via phone', {
      URL,
      affinity
    });

    setOpenAgencyHelpModal(true);
  };

  const shouldDisplay = useMemo(() => {
    return location.pathname === '/quote' && !quotePage;
  }, [location.pathname, quotePage]);

  const helpWidget = appConfig.helpWidget;
  if ((!isBranchEnv && !helpWidget) || shouldDisplay) {
    return null;
  }

  const chatLabel = session.isInternalAgent ? 'Internal Help Desk' : 'Open Agency Support Chat';

  return (
    <div className={classes.floatingContainer}>
      {openAgencyHelpModal && (
        <AgencyHelpModal setOpenAgencyHelpModal={setOpenAgencyHelpModal} username={session?.user?.username || ''} />
      )}
      <SpeedDial
        open={open}
        onClick={handleClick}
        onClose={handleClose}
        onOpen={handleOpen}
        icon={<HelpOutlineOutlinedIcon />}
        ariaLabel="Staff Speed Dial"
      >
        {open && !isBranchEnv && helpWidget?.fnolUrl && (
          <SpeedDialAction
            key="Submit a claim (FNOL)"
            icon={<AssignmentOutlinedIcon style={{ color: theme.colorPalette.green_75 }} />}
            tooltipTitle="Submit a claim (FNOL)"
            TooltipClasses={{ tooltip: classes.tooltip }}
            onClick={openFnolForm}
          />
        )}
        {open && (isBranchEnv ? !session.isInternalAgent : helpWidget?.showEmail) && (
          <SpeedDialAction
            key="Submit a help request"
            icon={<EmailIcon style={{ color: theme.colorPalette.green_75 }} />}
            tooltipTitle="Submit a help request"
            TooltipClasses={{ tooltip: classes.tooltip }}
            onClick={handleBugReport}
          />
        )}
        {open && (isBranchEnv ? !session.isInternalAgent : helpWidget?.showPhone) && (
          <SpeedDialAction
            key="Show agency support contact number"
            icon={<CallIcon style={{ color: theme.colorPalette.green_75 }} />}
            TooltipClasses={{ tooltip: classes.tooltip }}
            tooltipTitle="Show agency support contact number"
            onClick={showAgencyModal}
          />
        )}
        {open && (isBranchEnv || helpWidget?.showChat) && (
          <SpeedDialAction
            key={chatLabel}
            icon={<ChatIcon style={{ color: theme.colorPalette.green_75 }} />}
            TooltipClasses={classes}
            tooltipTitle={chatLabel}
            onClick={openChat}
          />
        )}
      </SpeedDial>
    </div>
  );
};

StaffSpeedDial.propTypes = {
  quotePage: PropTypes.bool
};

export default StaffSpeedDial;
